import { useEffect, useState, useCallback } from "react";
import ListMenu from "./ListMenu/ListMenu";
import "./List.css";
import useSize from "../../../../Utils/useSize/useSize";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import Filter from "./Filter/Filter";
import ListCard from "./ListCard";
import NotFound from "../../../../Utils/NotFound/NotFound";
import Loading from "../../../../Utils/Loading/Loading";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const fmt = require("indian-number-format");
const backend = await new Backend().init();

export default function MasterList({ super_id, userType }) {
  const windowSize = useSize();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState([]);
  const [userSearch, setUserSearch] = useState([]);

  useEffect(() => {
    loadUser();
  }, [super_id]);

  // 🧩 Corrected conditional logic
  const loadUser = useCallback(async () => {
    setLoading(true);

    const baseData = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
    };

    let data;

    // 🔧 Fix incorrect ternary nesting
    if (userType === 2) {
      data = { ...baseData, super_id: baseData.id };
    } else if (super_id) {
      data = { ...baseData, super_id };
    } else {
      data = { ...baseData, admin_id: localStorage.getItem("id") };
    }

    try {
      const response = await backend.load_master(data);
      setLoading(false);

      if (response.error === "False") {
        setUser(response.user || []);
        setUserSearch(response.user || []);
      } else if (response.logout === 1) {
        window.LogOut?.();
      } else {
        toast.error(response.message || "Failed to load users.");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error loading users:", error);
      toast.error("Server error. Please try again later.");
    }
  }, [super_id, userType]);

  const handleSearch = useCallback(
    (query) => {
      if (query.length > 1) {
        const filtered = userSearch.filter((u) =>
          u.username?.toLowerCase().includes(query.toLowerCase())
        );
        setUser(filtered);
      } else {
        setUser(userSearch);
      }
    },
    [userSearch]
  );

  // 🧾 PDF Export
  const downloadUserPDF = async () => {
    try {
      const pdfMakeModule = await import("pdfmake/build/pdfmake");
      const pdfFontsModule = await import("pdfmake/build/vfs_fonts");

      const pdfMake = pdfMakeModule.default || pdfMakeModule;
      const pdfFonts = pdfFontsModule.default || pdfFontsModule;
      pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs;

      const data = user;
      if (data.length === 0) return toast.error("No data available to export");

      const body = [
        [
          ...(userType === 1 ? [{ text: "Super", style: "tableHeader" }] : []),
          { text: "Master", style: "tableHeader" },
          { text: "Booked", style: "tableHeader" },
          { text: "Net-Booked", style: "tableHeader" },
          { text: "Sharing", style: "tableHeader" },
          { text: "Trade", style: "tableHeader" },
          { text: "Close Only", style: "tableHeader" },
          { text: "Status", style: "tableHeader" },
          { text: "IP Address", style: "tableHeader" },
          { text: "Created Date", style: "tableHeader" },
          { text: "Last Login", style: "tableHeader" },
        ],
        ...data.map((i) => [
          ...(userType === 1
            ? [{ text: i.super_username, style: "tableCell" }]
            : []),
          { text: i.username, style: "tableCell" },
          {
            text: fmt.format(i.profit_loss),
            style: "numericCell",
            color: parseFloat(i.profit_loss) > 0 ? "#399918" : "#FF0000",
          },
          {
            text: fmt.format(i.net_profit_loss),
            style: "numericCell",
            color: parseFloat(i.net_profit_loss) > 0 ? "#399918" : "#FF0000",
          },
          { text: i.sharing, style: "numericCell" },
          {
            text: i.trade === 1 ? "Active" : "Deactive",
            style: "numericCell",
            color: i.trade === 1 ? "#399918" : "#FF0000",
          },
          {
            text: i.only_sq === 1 ? "Active" : "Deactive",
            style: "numericCell",
            color: i.only_sq === 1 ? "#399918" : "#FF0000",
          },
          {
            text: i.status === 1 ? "Active" : "Deactive",
            style: "numericCell",
            color: i.status === 1 ? "#399918" : "#FF0000",
          },
          { text: i.ip || "-", style: "numericCell" },
          {
            text: i.date_created
              ? moment(i.date_created).format("DD-MM-YYYY HH:mm:ss")
              : "-",
            style: "numericCell",
          },
          {
            text: i.last_login_date
              ? moment(i.last_login_date).format("DD-MM-YYYY HH:mm:ss")
              : "-",
            style: "numericCell",
          },
        ]),
      ];

      const docDefinition = {
        pageSize: "A3",
        pageOrientation: "landscape",
        pageMargins: [40, 60, 40, 60],
        content: [
          {
            columns: [
              { image: "logo", width: 60 },
              {
                text: `${window.APP_NAME} MASTER LIST`,
                style: "header",
                width: "*",
                alignment: "right",
              },
            ],
            columnGap: 10,
          },
          {
            text: `Generated on: ${moment().format("DD-MMM-YYYY HH:mm")}`,
            style: "subHeader",
          },
          {
            table: {
              headerRows: 1,
              body,
            },
            layout: {
              fillColor: (rowIndex) =>
                rowIndex === 0
                  ? "#1E293B"
                  : rowIndex % 2 === 0
                  ? "#F8FAFC"
                  : null,
              hLineColor: "#CBD5E1",
              vLineColor: "#CBD5E1",
            },
          },
        ],
        images: {
          logo: window.LOGO,
        },
        styles: {
          header: {
            fontSize: 18,
            bold: true,
            color: "#1E293B",
            margin: [0, 0, 0, 10],
          },
          subHeader: {
            fontSize: 10,
            color: "#64748B",
            margin: [0, 0, 0, 10],
          },
          tableHeader: {
            bold: true,
            fontSize: 11,
            color: "#FFFFFF",
            fillColor: "#1E293B",
            alignment: "center",
          },
          tableCell: {
            fontSize: 10,
            margin: [0, 5, 0, 5],
          },
          numericCell: {
            fontSize: 10,
            margin: [0, 5, 0, 5],
            alignment: "right",
          },
        },
        footer: (currentPage, pageCount) => ({
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: "center",
          fontSize: 9,
          color: "#64748B",
        }),
      };

      pdfMake
        .createPdf(docDefinition)
        .download(`${moment().format("DD-MMM")}-Master-List.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    }
  };

  // 📊 Excel Export (fixed syntax)
  const downloadExcel = () => {
    if (!user || user.length === 0) return toast.error("No data to export");

    const data = user.map((item) => ({
      ...(userType === 1 ? { Super: item.super_username } : {}),
      Username: item.username,
      Booked: item.profit_loss,
      NetBooked: item.net_profit_loss,
      Sharing: item.sharing,
      Trade: item.trade === 1 ? "Active" : "Deactive",
      CloseOnly: item.only_sq === 1 ? "Active" : "Deactive",
      Status: item.status === 1 ? "Active" : "Deactive",
      Ip: item.ip || "-",
      CreatedDate: moment(item.date_created).format("DD-MM-YYYY HH:mm:ss"),
      LastLogin: moment(item.last_login_date).format("DD-MM-YYYY HH:mm:ss"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Master-${moment().format("DD-MMM-YYYY")}.xlsx`);
  };

  return (
    <div
      style={{
        height: windowSize[1] - 155,
        width: windowSize[0],
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Filter
        records={user.length}
        search={handleSearch}
        downloadUserPDF={downloadUserPDF}
        downloadExcel={downloadExcel}
      />
      <ListMenu userType={userType} />

      {loading ? (
        <div className="loadingContainer">
          <Loading height={windowSize[1] - 220} />
        </div>
      ) : user.length > 0 ? (
        <div
          style={{
            height: windowSize[1] - 220,
            width: windowSize[0],
            overflowY: "auto",
          }}
        >
          {user.map((item, index) => (
            <ListCard
              key={item.id || index}
              item={item}
              index={index}
              reload={loadUser}
              userType={userType}
            />
          ))}
        </div>
      ) : (
        <NotFound height={windowSize[1] - 220} title="Nothing here" subtext="No User Summary Found." />
      )}
    </div>
  );
}
